import React, { useState, useEffect, useCallback } from 'react';
import { 
  Activity, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  TrendingUp, TrendingDown, Clock, Users, Truck, Route, Map,
  Database, Wifi, WifiOff, Zap, BarChart3, ArrowUpRight,
  ArrowDownRight, Timer, Server, HardDrive, Loader2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const TruckDBMonitoringDashboard = ({ user }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  const [status, setStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [stats, setStats] = useState({
    totalSyncs: 0,
    successRate: 0,
    avgResponseTime: 0,
    lastDayOperations: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchAllData = useCallback(async (showLoader = false) => {
    if (showLoader) setRefreshing(true);
    
    try {
      const headers = { 'Authorization': 'Bearer cookie-auth' };

      // Fetch status
      const statusRes = await fetch(`${BACKEND_URL}/api/external/status`, { headers });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setStatus(statusData);
      }

      // Fetch sync history
      const historyRes = await fetch(`${BACKEND_URL}/api/external/sync-history?limit=20`, { headers });
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setSyncHistory(historyData.history || []);
        
        // Calculate stats from history
        const history = historyData.history || [];
        if (history.length > 0) {
          const successCount = history.filter(h => h.success).length;
          setStats(prev => ({
            ...prev,
            totalSyncs: history.length,
            successRate: Math.round((successCount / history.length) * 100),
            avgResponseTime: Math.round(history.reduce((acc, h) => acc + (h.duration_ms || 0), 0) / history.length),
            lastDayOperations: history.filter(h => {
              const syncDate = new Date(h.timestamp);
              const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
              return syncDate > dayAgo;
            }).length
          }));
        }
      }

      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchAllData(true);
  }, [fetchAllData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchAllData(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchAllData]);

  const triggerSync = async (type) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/external/sync/${type}`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });

      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Synchronisation réussie',
          description: `${data.count || data.drivers_count || data.vehicles_count || 0} éléments synchronisés`
        });
        fetchAllData(true);
      } else {
        throw new Error(data.detail || 'Erreur de synchronisation');
      }
    } catch (error) {
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    }
  };

  const getStatusColor = (isConnected) => {
    return isConnected ? 'text-green-500' : 'text-red-500';
  };

  const getStatusBg = (isConnected) => {
    return isConnected ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const isConnected = status?.configured && status?.connection_ok;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Activity className="w-7 h-7 text-blue-600" />
            Monitoring Truck Database
          </h2>
          <p className="text-gray-500 mt-1">
            Surveillance en temps réel de la synchronisation
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Auto-refresh toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            Auto-refresh
          </label>
          
          {/* Manual refresh */}
          <button
            onClick={() => fetchAllData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Connection Status Banner */}
      <div className={`rounded-xl p-4 border-2 ${getStatusBg(isConnected)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {isConnected ? (
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Wifi className="w-6 h-6 text-green-600" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <WifiOff className="w-6 h-6 text-red-600" />
              </div>
            )}
            <div>
              <h3 className={`text-lg font-semibold ${isConnected ? 'text-green-900' : 'text-red-900'}`}>
                {isConnected ? 'Connexion Active' : 'Connexion Inactive'}
              </h3>
              <p className={`text-sm ${isConnected ? 'text-green-700' : 'text-red-700'}`}>
                {isConnected 
                  ? `API Truck Database connectée et fonctionnelle`
                  : 'Vérifiez la configuration de l\'API'}
              </p>
            </div>
          </div>
          
          {lastUpdate && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Dernière mise à jour</p>
              <p className="text-sm font-medium text-gray-700">
                {lastUpdate.toLocaleTimeString('fr-FR')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Success Rate */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <span className={`flex items-center gap-1 text-sm font-medium ${
              stats.successRate >= 90 ? 'text-green-600' : 
              stats.successRate >= 70 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {stats.successRate >= 90 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {stats.successRate}%
            </span>
          </div>
          <h4 className="text-2xl font-bold text-gray-900 mt-3">{stats.successRate}%</h4>
          <p className="text-sm text-gray-500">Taux de réussite</p>
        </div>

        {/* Total Syncs */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <span className="flex items-center gap-1 text-sm font-medium text-blue-600">
              <ArrowUpRight className="w-4 h-4" />
              +{stats.lastDayOperations}
            </span>
          </div>
          <h4 className="text-2xl font-bold text-gray-900 mt-3">{stats.totalSyncs}</h4>
          <p className="text-sm text-gray-500">Synchronisations totales</p>
        </div>

        {/* Avg Response Time */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Timer className="w-5 h-5 text-purple-600" />
            </div>
            <span className={`flex items-center gap-1 text-sm font-medium ${
              stats.avgResponseTime < 500 ? 'text-green-600' : 
              stats.avgResponseTime < 1000 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {stats.avgResponseTime < 500 ? 'Rapide' : stats.avgResponseTime < 1000 ? 'Normal' : 'Lent'}
            </span>
          </div>
          <h4 className="text-2xl font-bold text-gray-900 mt-3">{stats.avgResponseTime}ms</h4>
          <p className="text-sm text-gray-500">Temps de réponse moyen</p>
        </div>

        {/* Last 24h */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <span className="flex items-center gap-1 text-sm font-medium text-orange-600">
              24h
            </span>
          </div>
          <h4 className="text-2xl font-bold text-gray-900 mt-3">{stats.lastDayOperations}</h4>
          <p className="text-sm text-gray-500">Syncs dernières 24h</p>
        </div>
      </div>

      {/* Data Sources */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Drivers */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900">Chauffeurs</h4>
              <p className="text-xs text-blue-600">Truck Database</p>
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-3xl font-bold text-blue-900">
                {status?.drivers?.cached_count || 0}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {status?.drivers?.last_update 
                  ? `Sync: ${new Date(status.drivers.last_update).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                  : 'Non synchronisé'}
              </p>
            </div>
            <button
              onClick={() => triggerSync('drivers')}
              className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              title="Synchroniser"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Vehicles */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-green-900">Véhicules</h4>
              <p className="text-xs text-green-600">Truck Database</p>
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-3xl font-bold text-green-900">
                {status?.vehicles?.cached_count || 0}
              </p>
              <p className="text-xs text-green-600 mt-1">
                {status?.vehicles?.last_update 
                  ? `Sync: ${new Date(status.vehicles.last_update).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                  : 'Non synchronisé'}
              </p>
            </div>
            <button
              onClick={() => triggerSync('vehicles')}
              className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              title="Synchroniser"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Routes */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center">
              <Route className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-purple-900">Routes</h4>
              <p className="text-xs text-purple-600">Pré-calculées</p>
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-3xl font-bold text-purple-900">
                {status?.routes?.cached_count || 0}
              </p>
              <p className="text-xs text-purple-600 mt-1">
                {status?.routes?.cities_count || 0} villes
              </p>
            </div>
            <button
              onClick={() => triggerSync('routes')}
              className="p-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              title="Synchroniser"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Vehicle Types */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center">
              <Map className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-orange-900">Types véhicules</h4>
              <p className="text-xs text-orange-600">Mapping</p>
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-3xl font-bold text-orange-900">
                {status?.vehicle_types?.cached_count || 0}
              </p>
              <p className="text-xs text-orange-600 mt-1">
                {status?.vehicle_types?.mapping_count || 0} mappings
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Activité récente
          </h3>
          <span className="text-sm text-gray-500">
            {syncHistory.length} dernières synchronisations
          </span>
        </div>
        
        <div className="divide-y max-h-96 overflow-y-auto">
          {syncHistory.length > 0 ? (
            syncHistory.map((item, index) => (
              <div key={index} className="px-6 py-4 hover:bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {item.success ? (
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <XCircle className="w-4 h-4 text-red-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">
                      {item.type === 'drivers' && 'Synchronisation chauffeurs'}
                      {item.type === 'vehicles' && 'Synchronisation véhicules'}
                      {item.type === 'routes' && 'Synchronisation routes'}
                      {item.type === 'operation' && 'Push opération'}
                      {!['drivers', 'vehicles', 'routes', 'operation'].includes(item.type) && item.type}
                    </p>
                    <p className="text-sm text-gray-500">
                      {item.direction === 'push' ? 'PUSH →' : 'PULL ←'} {item.count || 0} éléments
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(item.timestamp).toLocaleTimeString('fr-FR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(item.timestamp).toLocaleDateString('fr-FR', { 
                      day: '2-digit', 
                      month: 'short' 
                    })}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-12 text-center text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucune synchronisation récente</p>
              <p className="text-sm mt-1">Les activités apparaîtront ici</p>
            </div>
          )}
        </div>
      </div>

      {/* System Health */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Server className="w-5 h-5 text-gray-500" />
          État du système
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* API Connection */}
          <div className="flex items-center gap-4">
            <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
            <div>
              <p className="font-medium text-gray-900">API Truck Database</p>
              <p className="text-sm text-gray-500">
                {isConnected ? 'Connecté' : 'Déconnecté'}
              </p>
            </div>
          </div>

          {/* Cache Status */}
          <div className="flex items-center gap-4">
            <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
            <div>
              <p className="font-medium text-gray-900">Cache local</p>
              <p className="text-sm text-gray-500">
                TTL: 5 minutes
              </p>
            </div>
          </div>

          {/* Database */}
          <div className="flex items-center gap-4">
            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
            <div>
              <p className="font-medium text-gray-900">Base de données</p>
              <p className="text-sm text-gray-500">
                MongoDB - Opérationnel
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TruckDBMonitoringDashboard;
